// @ts-nocheck
import { Card, Code, Divider, Group, SimpleGrid, Stack, Text, Title } from '@/components/ui';
import {
  StatCard,
  MetricGrid,
  ChartCard,
  ActivityFeed,
} from '@/components/advanced/dashboard';
import {
  IconCash,
  IconUsers,
  IconShoppingCart,
  IconChartBar,
} from '@tabler/icons-react';

export default function DashboardComponentsPage() {
  // StatCard mock data
  const statTrend = { value: 12.5, label: 'vs last month' };
  const statProgress = { value: 68, label: '68%' };

  // MetricGrid mock data (each entry is a StatCardProps)
  const metrics = [
    {
      title: 'Total Revenue',
      value: '$48,920',
      icon: <IconCash size={22} />,
      trend: { value: 8.2, label: 'vs last month' },
    },
    {
      title: 'Active Users',
      value: '2,340',
      icon: <IconUsers size={22} />,
      trend: { value: 3.1, label: 'this week' },
    },
    {
      title: 'Orders',
      value: 1284,
      icon: <IconShoppingCart size={22} />,
      trend: { value: -2.4, label: 'vs last week' },
    },
    {
      title: 'Conversion',
      value: '3.6%',
      icon: <IconChartBar size={22} />,
      trend: { value: 0, label: 'no change' },
    },
  ];

  // ChartCard time range state (kept local/self-contained)
  const timeRanges = [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 90 days' },
  ];

  // ActivityFeed mock data
  const activityItems = [
    {
      id: '1',
      type: 'comment',
      user: { name: 'Pam Beesly', online: true },
      action: 'commented on',
      target: 'Q2 Budget Plan',
      content: 'Looks good, but we should double-check the travel line item.',
      timestamp: new Date(Date.now() - 1000 * 60 * 3),
      unread: true,
    },
    {
      id: '2',
      type: 'create',
      user: { name: 'Jim Halpert' },
      action: 'created task',
      target: 'Onboard new vendor',
      timestamp: new Date(Date.now() - 1000 * 60 * 45),
    },
    {
      id: '3',
      type: 'like',
      user: { name: 'Oscar Martinez', online: true },
      action: 'approved',
      target: 'Expense Report #4821',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 5),
    },
    {
      id: '4',
      type: 'share',
      user: { name: 'Angela Martin' },
      action: 'shared',
      target: 'Year-end Audit Checklist',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 26),
    },
  ];

  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Dashboard Components</Title>
        <Text c="dimmed" size="sm">
          Building blocks for analytics dashboards: stat cards, metric grids,
          chart containers, and activity feeds.
        </Text>
      </Stack>

      {/* StatCard */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>StatCard</Title>
            <Text size="sm" c="dimmed">
              A single KPI tile with optional trend, progress ring, icon, and
              visual variants
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
              <StatCard
                title="Revenue"
                value="$48,920"
                icon={<IconCash size={22} />}
                trend={statTrend}
              />
              <StatCard
                title="Storage Used"
                value="68 GB"
                description="of 100 GB"
                progress={statProgress}
              />
              <StatCard
                title="New Signups"
                value="2,340"
                variant="outlined"
                trend={{ value: -4.2, label: 'vs last week' }}
              />
              <StatCard
                title="Uptime"
                value="99.98%"
                description="last 30 days"
                variant="gradient"
                gradientVariant="primary"
                trend={{ value: 0.1, label: 'improved' }}
              />
            </SimpleGrid>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<StatCard
  title="Revenue"
  value="$48,920"
  icon={<IconCash size={22} />}
  trend={{ value: 12.5, label: 'vs last month' }}
/>

<StatCard
  title="Storage Used"
  value="68 GB"
  description="of 100 GB"
  progress={{ value: 68, label: '68%' }}
/>

<StatCard
  title="Uptime"
  value="99.98%"
  variant="gradient"
  gradientVariant="primary"
  trend={{ value: 0.1, label: 'improved' }}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • title / value - required label and metric
              <br />
              • description - optional supporting text
              <br />
              • icon - optional ReactNode shown top-right
              <br />
              • trend - {`{ value, label? }`} colored up/down indicator
              <br />
              • progress - {`{ value, label? }`} renders a ring instead of icon
              <br />
              • variant - 'default' | 'gradient' | 'outlined' | 'minimal'
              <br />
              • gradientVariant - 'primary' | 'secondary' | 'tertiary'
              <br />
              • size - 'sm' | 'md' | 'lg'
              <br />
              • onClick - makes the card clickable
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* MetricGrid */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>MetricGrid</Title>
            <Text size="sm" c="dimmed">
              Responsive grid that renders an array of StatCards from a single
              metrics array
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <MetricGrid
              metrics={metrics}
              columns={{ base: 1, sm: 2, lg: 4 }}
              spacing="md"
            />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`const metrics = [
  {
    title: 'Total Revenue',
    value: '$48,920',
    icon: <IconCash size={22} />,
    trend: { value: 8.2, label: 'vs last month' },
  },
  {
    title: 'Active Users',
    value: '2,340',
    icon: <IconUsers size={22} />,
    trend: { value: 3.1, label: 'this week' },
  },
  // ... more StatCardProps entries
];

<MetricGrid
  metrics={metrics}
  columns={{ base: 1, sm: 2, lg: 4 }}
  spacing="md"
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • metrics - array of StatCardProps (required)
              <br />
              • columns - number or responsive {`{ base, sm, md, lg, xl }`}
              <br />
              • spacing / verticalSpacing - grid gaps
              <br />
              • gradientVariant - applied to all cards
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* ChartCard */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>ChartCard</Title>
            <Text size="sm" c="dimmed">
              A titled container for charts with a time-range selector, an
              actions menu, and loading/error/footer slots. Drop any chart in as
              children.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <ChartCard
              title="Monthly Sales"
              subtitle="Revenue over the selected period"
              height={220}
              timeRanges={timeRanges}
              selectedTimeRange="30d"
              onTimeRangeChange={() => {}}
              onRefresh={() => {}}
              onExport={() => {}}
              footerContent={
                <Text size="xs" c="dimmed">
                  Updated just now
                </Text>
              }
            >
              {/* Placeholder chart body - replace with a real chart */}
              <Group justify="space-around" align="flex-end" h="100%" px="md">
                {[40, 65, 50, 80, 60, 95, 70].map((h, i) => (
                  <div
                    key={i}
                    style={{
                      width: 28,
                      height: `${h}%`,
                      borderRadius: 6,
                      background:
                        'linear-gradient(180deg, #4dabf7, #1c7ed6)',
                    }}
                  />
                ))}
              </Group>
            </ChartCard>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`<ChartCard
  title="Monthly Sales"
  subtitle="Revenue over the selected period"
  height={220}
  timeRanges={[
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 90 days' },
  ]}
  selectedTimeRange="30d"
  onTimeRangeChange={(value) => setRange(value)}
  onRefresh={() => refetch()}
  onExport={() => exportCsv()}
  footerContent={<Text size="xs" c="dimmed">Updated just now</Text>}
>
  {/* Render any chart component here */}
  <MyChart data={data} />
</ChartCard>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Key Props:</Text>
            <Text size="xs" c="dimmed" component="div">
              • title / subtitle - header text
              <br />
              • children - the chart content (required)
              <br />
              • height - chart body height (number or string)
              <br />
              • loading / error - render overlay states
              <br />
              • timeRanges + selectedTimeRange + onTimeRangeChange - range picker
              <br />
              • onRefresh / onExport / onExpand / onFilter - menu actions
              <br />
              • headerActions / footerContent - custom slots
              <br />
              • variant - 'default' | 'elevated' | 'outlined'
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* ActivityFeed */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>ActivityFeed</Title>
            <Text size="sm" c="dimmed">
              Scrollable, time-stamped stream of user activity with avatars,
              unread highlighting, and load-more support
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>Example:</Text>
            <ActivityFeed
              items={activityItems}
              title="Recent Activity"
              maxHeight={300}
              variant="detailed"
              hasMore
              onLoadMore={() => {}}
              onItemClick={() => {}}
            />
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Code:</Text>
            <Code block language="tsx">
{`const items = [
  {
    id: '1',
    type: 'comment',
    user: { name: 'Pam Beesly', online: true },
    action: 'commented on',
    target: 'Q2 Budget Plan',
    content: 'Looks good, but check the travel line item.',
    timestamp: new Date(),
    unread: true,
  },
  {
    id: '2',
    type: 'create',
    user: { name: 'Jim Halpert' },
    action: 'created task',
    target: 'Onboard new vendor',
    timestamp: new Date(),
  },
  // ... more items
];

<ActivityFeed
  items={items}
  title="Recent Activity"
  maxHeight={300}
  variant="detailed"
  hasMore
  onLoadMore={() => loadMore()}
  onItemClick={(item) => openItem(item)}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>ActivityItem Interface:</Text>
            <Code block language="typescript">
{`interface ActivityItem {
  id: string;
  type: 'comment' | 'like' | 'share' | 'create'
      | 'update' | 'delete' | 'mention' | 'follow' | 'custom';
  user: { name: string; avatar?: string; online?: boolean };
  action: string;
  target?: string;
  content?: string;
  timestamp: Date | string;
  icon?: React.ReactNode;
  metadata?: Record<string, any>;
  unread?: boolean;
}`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      <Divider />

      {/* Widget Dashboard System - explained, not rendered */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>Widget Dashboard System</Title>
            <Text size="sm" c="dimmed">
              The barrel also exports a full drag-and-drop widget dashboard
              (DashboardProvider, DashboardGrid, DashboardToolbar, WidgetPicker,
              the widget/container registries, and hooks like useBreakpoint /
              useDragDrop).
            </Text>
          </Stack>

          <Text size="sm">
            This system is not rendered live here because it is stateful and
            data-bound: DashboardGrid reads layout from the global
            <Code>useDashboardStore</Code> (Zustand) and the auto-registered
            widgets (InvoiceStats, LatestOrders, ProjectStats, UpcomingMeetings,
            etc.) expect global dashboard data / SDK-backed sources rather than
            inline mock arrays. Showing it safely would require seeding the
            store and providing a data fetcher, which is out of scope for this
            self-contained showcase.
          </Text>

          <Stack gap="xs">
            <Text size="sm" fw={500}>Typical setup:</Text>
            <Code block language="tsx">
{`import {
  DashboardProvider,
  DashboardGrid,
  DashboardToolbar,
} from '@/components/advanced/dashboard';

<DashboardProvider initialData={globalData}>
  <DashboardToolbar />
  <DashboardGrid globalData={globalData} />
</DashboardProvider>`}
            </Code>
          </Stack>
        </Stack>
      </Card>

      {/* Use Cases */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Common Use Cases</Title>

          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
            <Stack gap="xs">
              <Text size="sm" fw={500}>KPI Overview</Text>
              <Text size="xs" c="dimmed">
                Use MetricGrid + StatCard at the top of a dashboard to surface
                headline numbers with trend indicators at a glance.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Analytics Panels</Text>
              <Text size="xs" c="dimmed">
                Wrap charts in ChartCard to get a consistent header, time-range
                picker, and export/refresh actions across every panel.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>Live Activity</Text>
              <Text size="xs" c="dimmed">
                Use ActivityFeed in a sidebar to show recent comments, approvals,
                and mentions with unread badges and infinite scroll.
              </Text>
            </Stack>
          </SimpleGrid>
        </Stack>
      </Card>
    </Stack>
  );
}
